import {
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  Renderer2,
} from '@angular/core';

@Directive({
  selector: '[appRipple]',
  host: {
    '(mousedown)': 'onMouseDown($event)',
    '(touchstart)': 'onTouchStart($event)',
  },
})
export class RippleDirective implements OnDestroy {
  @Input() appRippleDisabled = false;
  @Input() appRippleColor = 'rgba(0, 0, 0, 0.1)';
  @Input() appRippleCentered = true;

  private activeRipples = new Set<HTMLElement>();
  private pointerUpListeners: (() => void)[] = [];
  private reducedMotion = false;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone
  ) {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }
    this.setupHostStyles();
  }

  onMouseDown(event: MouseEvent): void {
    if (this.appRippleDisabled) {
      return;
    }
    const ripple = this.createRipple(event.clientX, event.clientY);
    this.bindPointerUpEvents(ripple, 'mouse');
  }

  onTouchStart(event: TouchEvent): void {
    if (this.appRippleDisabled) {
      return;
    }
    const touch = event.touches[0];
    const ripple = this.createRipple(touch.clientX, touch.clientY);
    this.bindPointerUpEvents(ripple, 'touch');
  }

  ngOnDestroy(): void {
    this.removeAllRipples();
    this.removePointerUpListeners();
  }

  private createRipple(clientX: number, clientY: number): HTMLElement {
    const host = this.elementRef.nativeElement;
    const rect = host.getBoundingClientRect();

    let x: number;
    let y: number;

    if (this.appRippleCentered) {
      x = rect.width / 2;
      y = rect.height / 2;
    } else {
      x = clientX - rect.left;
      y = clientY - rect.top;
    }

    const radius = this.calculateRadius(x, y, rect.width, rect.height);
    const diameter = radius * 2;

    const ripple = this.renderer.createElement('span') as HTMLElement;

    const styles: Record<string, string> = {
      position: 'absolute',
      'border-radius': '50%',
      'pointer-events': 'none',
      'background-color': this.appRippleColor,
      width: `${diameter}px`,
      height: `${diameter}px`,
      left: `${x - radius}px`,
      top: `${y - radius}px`,
      transform: 'scale(0)',
      opacity: '1',
    };

    if (!this.reducedMotion) {
      styles['transition'] =
        'transform 450ms cubic-bezier(0, 0, 0.2, 1), opacity 400ms cubic-bezier(0, 0, 0.2, 1)';
    }

    Object.entries(styles).forEach(([prop, value]) => {
      this.renderer.setStyle(ripple, prop, value);
    });

    this.renderer.appendChild(host, ripple);
    this.activeRipples.add(ripple);

    // Force reflow to ensure initial styles are applied before transition
    void ripple.offsetWidth;

    this.renderer.setStyle(ripple, 'transform', 'scale(1)');

    return ripple;
  }

  private fadeOutRipple(ripple: HTMLElement): void {
    if (!this.activeRipples.has(ripple)) {
      return;
    }

    this.renderer.setStyle(ripple, 'opacity', '0');

    if (this.reducedMotion) {
      this.removeRipple(ripple);
      return;
    }

    const onTransitionEnd = () => {
      ripple.removeEventListener('transitionend', onTransitionEnd);
      this.removeRipple(ripple);
    };

    ripple.addEventListener('transitionend', onTransitionEnd);
  }

  private removeRipple(ripple: HTMLElement): void {
    this.activeRipples.delete(ripple);
    if (ripple.parentNode) {
      this.renderer.removeChild(ripple.parentNode, ripple);
    }
  }

  private bindPointerUpEvents(
    ripple: HTMLElement,
    type: 'mouse' | 'touch'
  ): void {
    this.ngZone.runOutsideAngular(() => {
      if (type === 'mouse') {
        const onMouseUp = () => {
          this.fadeOutRipple(ripple);
          document.removeEventListener('mouseup', onMouseUp);
          document.removeEventListener('mouseleave', onMouseUp);
          this.pointerUpListeners = this.pointerUpListeners.filter(
            (fn) => fn !== cleanup
          );
        };
        const cleanup = () => {
          document.removeEventListener('mouseup', onMouseUp);
          document.removeEventListener('mouseleave', onMouseUp);
        };
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mouseleave', onMouseUp);
        this.pointerUpListeners.push(cleanup);
      } else {
        const onTouchEnd = () => {
          this.fadeOutRipple(ripple);
          document.removeEventListener('touchend', onTouchEnd);
          document.removeEventListener('touchcancel', onTouchEnd);
          this.pointerUpListeners = this.pointerUpListeners.filter(
            (fn) => fn !== cleanup
          );
        };
        const cleanup = () => {
          document.removeEventListener('touchend', onTouchEnd);
          document.removeEventListener('touchcancel', onTouchEnd);
        };
        document.addEventListener('touchend', onTouchEnd);
        document.addEventListener('touchcancel', onTouchEnd);
        this.pointerUpListeners.push(cleanup);
      }
    });
  }

  private calculateRadius(
    x: number,
    y: number,
    width: number,
    height: number
  ): number {
    const distX = Math.max(x, width - x);
    const distY = Math.max(y, height - y);
    return Math.sqrt(distX * distX + distY * distY);
  }

  private setupHostStyles(): void {
    const host = this.elementRef.nativeElement;
    const computedPosition = getComputedStyle(host).position;
    if (!computedPosition || computedPosition === 'static') {
      this.renderer.setStyle(host, 'position', 'relative');
    }
    this.renderer.setStyle(host, 'overflow', 'hidden');
  }

  private removeAllRipples(): void {
    this.activeRipples.forEach((ripple) => {
      if (ripple.parentNode) {
        this.renderer.removeChild(ripple.parentNode, ripple);
      }
    });
    this.activeRipples.clear();
  }

  private removePointerUpListeners(): void {
    this.pointerUpListeners.forEach((cleanup) => cleanup());
    this.pointerUpListeners = [];
  }
}
